'use client'

import { Children } from 'react'
import { clsx } from 'clsx'
import type { SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { storyblokEditable } from '@storyblok/react'
import { type StoryblokRichTextNode } from '@storyblok/richtext'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import { Heading, type HeadingProps } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { type TypographyVariant } from 'ui/src/components/Typography/Typography'
import { InfoIconOutlined } from 'ui/src/icons/InfoIconOutlined'
import { xStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { renderRichTextToJsx } from '../RichTextBlock/richTextReactRenderer'
import { firstLinePrimaryColorStyles, multicolorCharacterStyles } from './HeadingBlock.css'

// Special case: Skurt campaign page heading
function MulticolorHeadingText({ text }: { text: string }) {
  return (
    <>
      {Array.from(text).map((character, index) => {
        if (character === '\n') return <br key={`${index}-newline`} />
        if (character === ' ') return <span key={`${index}-space`}> </span>
        return (
          <span key={`${index}-${character}`} className={multicolorCharacterStyles}>
            {character}
          </span>
        )
      })}
    </>
  )
}

export type HeadingBlockProps = SbBaseBlockProps<{
  text: string
  as: HeadingProps['as']
  color: HeadingProps['color']
  id?: string
  firstLinePrimaryColor?: HeadingProps['color']
  isMulticolor?: boolean
  variant?: TypographyVariant
  variantDesktop?: TypographyVariant
  textAlignment?: HeadingProps['align']
  balance?: boolean
  tooltip?: StoryblokRichTextNode
}>

export function HeadingBlock({ blok, nested }: HeadingBlockProps) {
  const isLarge = useBreakpoint('lg')

  // Gotcha: we may get empty string from Storyblok for 'color' and 'textAlignment', this should handled safely

  const color = blok.color || 'textPrimary'

  const align = blok.textAlignment || 'center'

  const tooltipContent = blok.tooltip ? renderRichTextToJsx(blok.tooltip) : null

  const isMulticolor = Boolean(blok.isMulticolor)

  return (
    <ConditionalWrapper
      condition={!nested}
      wrapWith={(children) => <div className={sprinkles({ paddingInline: 'md' })}>{children}</div>}
    >
      <ConditionalWrapper
        condition={!!blok.tooltip}
        wrapWith={(children) => <div className={xStack({ alignItems: 'center', gap: 'xs' })}>{children}</div>}
      >
        <Heading
          id={blok.id}
          as={blok.as}
          variant={{ _: blok.variant ?? 'standard.32', md: blok.variantDesktop ?? 'standard.40' }}
          color={color}
          align={align}
          balance={blok.balance}
          className={clsx(blok.firstLinePrimaryColor && !isMulticolor && firstLinePrimaryColorStyles)}
          {...storyblokEditable(blok)}
        >
          {isMulticolor ? <MulticolorHeadingText text={blok.text ?? ''} /> : blok.text}
        </Heading>

        {tooltipContent && (
          <ToggleTip
            // We show sticky header on mobile; tooltip should not be overridden by it
            side={isLarge ? 'top' : 'bottom'}
            align="start"
            alignOffset={-12}
            sideOffset={0}
            portal={false}
            collisionPadding={4}
            Trigger={
              <IconButton
                data-no-hover
                Icon={<InfoIconOutlined size="1.25rem" color={tokens.legacyColors.fillOpaqueSecondary} />}
              />
            }
          >
            {/* Edge case: renderRichTextToJsx can return an array of React elements. Therefore we need to make sure every single */}
            {/* child get rendered individually as a children of <Text> in order to 'asChild' to work as expected */}
            {Children.map(tooltipContent, (child) => (
              <Text size="xs" color="textNegative" asChild>
                {child}
              </Text>
            ))}
          </ToggleTip>
        )}
      </ConditionalWrapper>
    </ConditionalWrapper>
  )
}
HeadingBlock.blockName = 'heading'
